import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { isAuthenticated, logout } from '../utils/auth';
import PageHeader from './PageHeader';
import './History.css';

const fetchOptions = { credentials: 'include', headers: { 'Content-Type': 'application/json' } };

const DriverLink = ({ driver }) => {
  if (!driver) return <span>—</span>;
  if (driver._id) {
    return <Link to={`/drivers/${driver._id}`} className="driver-pick-link">{driver.name}</Link>;
  }
  return <span>{driver.name || '—'}</span>;
};

const History = () => {
  const [picks, setPicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterYear, setFilterYear] = useState(String(new Date().getFullYear()));
  const [sortOrder, setSortOrder] = useState('newest');
  const navigate = useNavigate();

  const handleUnauthorized = useCallback(() => {
    fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  }, [navigate]);

  const fetchPicks = useCallback(async () => {
    try {
      const response = await fetch('/api/picks', fetchOptions);
      if (response.status === 401) { handleUnauthorized(); return; }
      const data = await response.json();
      setPicks(data);
    } catch (error) {
      console.error('Error fetching picks:', error);
    } finally {
      setLoading(false);
    }
  }, [handleUnauthorized]);

  useEffect(() => {
    if (!isAuthenticated()) {
      navigate('/login');
      return;
    }
    fetchPicks();
  }, [navigate, fetchPicks]);

  const handleLogout = async () => {
    await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }).catch(() => {});
    logout();
    navigate('/login');
  };

  const availableYears = useMemo(() => {
    const years = picks
      .map(pick => pick.raceId?.startTime ? new Date(pick.raceId.startTime).getFullYear() : null)
      .filter(Boolean);
    const unique = [...new Set(years)].sort((a, b) => b - a);
    const currentYear = new Date().getFullYear();
    if (!unique.includes(currentYear)) unique.unshift(currentYear);
    return unique;
  }, [picks]);

  const filteredAndSortedPicks = picks
    .filter(pick => {
      if (filterStatus !== 'all' && pick.raceId?.status !== filterStatus) return false;
      if (filterYear !== 'all') {
        const year = pick.raceId?.startTime ? new Date(pick.raceId.startTime).getFullYear() : null;
        if (String(year) !== filterYear) return false;
      }
      return true;
    })
    .sort((a, b) => {
      const dateA = new Date(a.raceId?.startTime || 0);
      const dateB = new Date(b.raceId?.startTime || 0);
      return sortOrder === 'newest' ? dateB - dateA : dateA - dateB;
    });

  if (loading) {
    return <div className="history"><p>Loading history...</p></div>;
  }

  return (
    <div className="history">
      <div className="history-header">
        <PageHeader overline="Your Race Predictions" title="MY PREDICTIONS" />
        <div className="history-header-actions">
          <button onClick={() => navigate('/dashboard')} className="nav-button">
            ← Back to Dashboard
          </button>
          <button onClick={handleLogout} className="nav-button">
            Logout
          </button>
        </div>
      </div>

      <div className="history-controls">
        <div className="control-group">
          <label htmlFor="filter-status">Filter by status:</label>
          <select
            id="filter-status"
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            className="control-select"
          >
            <option value="all">All</option>
            <option value="completed">Completed</option>
            <option value="ongoing">Ongoing</option>
            <option value="upcoming">Upcoming</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="sort-order">Sort by date:</label>
          <select
            id="sort-order"
            value={sortOrder}
            onChange={e => setSortOrder(e.target.value)}
            className="control-select"
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
          </select>
        </div>
        <div className="control-group">
          <label htmlFor="filter-year">Filter by year:</label>
          <select
            id="filter-year"
            value={filterYear}
            onChange={e => setFilterYear(e.target.value)}
            className="control-select"
          >
            <option value="all">All Years</option>
            {availableYears.map(year => (
              <option key={year} value={String(year)}>{year}</option>
            ))}
          </select>
        </div>
      </div>

      {filteredAndSortedPicks.length === 0 ? (
        <p className="no-picks">
          {picks.length === 0
            ? 'No predictions made yet.'
            : `No predictions match the selected filters.`}
        </p>
      ) : (
        <div className="picks-grid">
          {filteredAndSortedPicks.map(pick => {
            const race = pick.raceId || {};
            const raceName = race.name || 'Unknown Race';
            const raceDate = race.startTime ? new Date(race.startTime) : null;
            const status = race.status || 'upcoming';

            return (
              <div key={pick._id} className="pick-card">
                <div className="pick-card-header">
                  <div>
                    <h2>
                      {race._id
                        ? <Link to={`/races/${race._id}`} className="race-name-link">{raceName}</Link>
                        : raceName}
                    </h2>
                    <p className="pick-circuit">{race.location || ''}</p>
                    {raceDate && (
                      <p className="pick-date">
                        {raceDate.toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                      </p>
                    )}
                  </div>
                  <span className={`status-badge status-${status}`}>{status}</span>
                </div>

                <div className="pick-section">
                  <h3>Your Picks</h3>
                  <ol className="picks-list">
                    <li><span className="position-label">1st</span> <DriverLink driver={pick.firstPlace} /></li>
                    <li><span className="position-label">2nd</span> <DriverLink driver={pick.secondPlace} /></li>
                    <li><span className="position-label">3rd</span> <DriverLink driver={pick.thirdPlace} /></li>
                  </ol>
                </div>

                {status === 'completed' && race.results && (
                  <div className="pick-section results-section">
                    <h3>Actual Podium</h3>
                    <ol className="picks-list">
                      <li><span className="position-label">1st</span> <DriverLink driver={race.results.first} /></li>
                      <li><span className="position-label">2nd</span> <DriverLink driver={race.results.second} /></li>
                      <li><span className="position-label">3rd</span> <DriverLink driver={race.results.third} /></li>
                    </ol>
                  </div>
                )}

                {status === 'completed' && (
                  <div className="pick-points">
                    <span className="points-label">Points Earned</span>
                    <span className="points-value">{pick.points ?? 0}</span>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default History;
